import React, { useEffect, useState } from "react";

import { lessonRequestService } from "api/services/lesson-request.service";
import Badge from "components/UI/Badge";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";
import {
  CANDIDATE_STATUS_BADGE,
  EVENT_TYPE_LABEL,
  LESSON_REQUEST_STATUS_BADGE,
} from "utils/constant/lesson-request";

import type {
  LessonRequestCandidateStatus,
  LessonRequestDetail,
  LessonRequestEventType,
} from "api/types/lesson-request.types";

import "./LessonRequestDetailModal.scss";

interface LessonRequestDetailModalProps {
  open: boolean;
  requestId: number | null;
  onClose: () => void;
}

const formatDateTime = (v: string | null): string => (v ? v.slice(0, 16).replace("T", " ") : "-");
const formatTime = (v: string): string => (v ? v.slice(0, 5) : "-");

interface FieldProps {
  label: string;
  full?: boolean;
  multiline?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, full, multiline, children }) => (
  <div className={`lr-field${full ? " lr-field--full" : ""}`}>
    <span className="lr-field__label">{label}</span>
    <div className={`lr-field__value${multiline ? " lr-field__value--multiline" : ""}`}>
      {children}
    </div>
  </div>
);

const LessonRequestDetailModal: React.FC<LessonRequestDetailModalProps> = ({
  open,
  requestId,
  onClose,
}) => {
  const [detail, setDetail] = useState<LessonRequestDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || requestId == null) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setDetail(null);
    lessonRequestService
      .get(requestId)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, requestId]);

  const renderContent = () => {
    if (loading) return <div className="admin-page__loading">불러오는 중...</div>;
    if (error) return <div className="admin-page__error">{error}</div>;
    if (!detail) return null;

    return (
      <div className="lr-detail">
        {/* 기본 정보 */}
        <section className="lr-section">
          <h4 className="lr-section__title">기본 정보</h4>
          <div className="lr-section__grid">
            <Field label="강습명" full>
              {detail.lesson_name || "-"}
            </Field>
            <Field label="수업종류">{detail.request_type_display}</Field>
            <Field label="종목">{detail.sport_name || "-"}</Field>
            <Field label="신청자">
              {detail.applicant_name}
              <span className="lr-muted"> ({detail.applicant_user_id})</span>
            </Field>
            <Field label="상태">
              <Badge variant={LESSON_REQUEST_STATUS_BADGE[detail.status] ?? "default"}>
                {detail.status_display}
              </Badge>
            </Field>
            <Field label="기준 브랜드">{detail.request_brand_name}</Field>
            <Field label="인원">{detail.participant_count}명</Field>
          </div>
        </section>

        {/* 희망 일정 · 장소 */}
        <section className="lr-section">
          <h4 className="lr-section__title">희망 일정 · 장소</h4>
          <div className="lr-section__grid">
            <Field label="희망 날짜">{detail.requested_date}</Field>
            <Field label="희망 시간">
              {formatTime(detail.requested_start_time)} ~ {formatTime(detail.requested_end_time)}
            </Field>
            <Field label="장소">{detail.location_name}</Field>
            <Field label="희망 회차">
              {detail.requested_day_order != null ? `Day ${detail.requested_day_order}` : "-"}
            </Field>
            <Field label="이론 수업">{detail.requested_is_theory ? "예" : "아니오"}</Field>
            <Field label="강사에게 남긴 메모" full multiline>
              {detail.requester_message || "-"}
            </Field>
          </div>
        </section>

        {/* 수락 내용 */}
        <section className="lr-section">
          <h4 className="lr-section__title">수락 내용</h4>
          <div className="lr-section__grid">
            <Field label="담당 강사">
              {detail.accepted_instructor_name ? (
                <span>
                  {detail.accepted_instructor_name}
                  <span className="lr-muted"> ({detail.accepted_instructor_user_id})</span>
                </span>
              ) : (
                "-"
              )}
            </Field>
            <Field label="오픈된 수업명">{detail.opened_schedule_title || "-"}</Field>
            <Field label="생성된 스케줄">{detail.schedule_id ?? "-"}</Field>
            <Field label="생성된 예약">{detail.reservation_id ?? "-"}</Field>
            <Field label="생성된 연습반">{detail.practice_schedule_id ?? "-"}</Field>
          </div>
        </section>

        {/* 히스토리 타임라인 */}
        <section className="lr-section">
          <h4 className="lr-section__title">히스토리</h4>
          {detail.events.length === 0 ? (
            <div className="lr-empty">이벤트가 없습니다.</div>
          ) : (
            <ul className="lr-timeline">
              {detail.events.map((ev, i) => (
                <li key={i} className="lr-timeline__item">
                  <span className="lr-timeline__dot" />
                  <div className="lr-timeline__body">
                    <div className="lr-timeline__head">
                      <span className="lr-timeline__type">
                        {EVENT_TYPE_LABEL[ev.event_type as LessonRequestEventType] ?? ev.event_type}
                      </span>
                      <span className="lr-timeline__time">{formatDateTime(ev.created_at)}</span>
                    </div>
                    <div className="lr-timeline__meta">
                      <span>행위자: {ev.actor_name ?? "시스템"}</span>
                      {(ev.from_status || ev.to_status) && (
                        <span className="lr-muted">
                          {ev.from_status || "―"} → {ev.to_status || "―"}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 후보 강사별 응답 */}
        <section className="lr-section">
          <h4 className="lr-section__title">후보 강사별 응답 ({detail.candidates.length})</h4>
          {detail.candidates.length === 0 ? (
            <div className="lr-empty">후보 강사가 없습니다.</div>
          ) : (
            <div className="lr-candidates">
              <table className="lr-candidates__table">
                <thead>
                  <tr>
                    <th>강사</th>
                    <th>상태</th>
                    <th>거절 사유</th>
                    <th>통지</th>
                    <th>열람</th>
                    <th>응답</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.candidates.map((c, i) => (
                    <tr key={i}>
                      <td>
                        {c.instructor_name}
                        <span className="lr-muted"> ({c.instructor_user_id})</span>
                      </td>
                      <td>
                        <Badge
                          variant={
                            CANDIDATE_STATUS_BADGE[c.status as LessonRequestCandidateStatus] ??
                            "default"
                          }
                        >
                          {c.status_display}
                        </Badge>
                      </td>
                      <td>{c.reject_reason || "-"}</td>
                      <td>{formatDateTime(c.notified_at)}</td>
                      <td>{formatDateTime(c.seen_at)}</td>
                      <td>{formatDateTime(c.responded_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* 기록 */}
        <section className="lr-section">
          <h4 className="lr-section__title">기록</h4>
          <div className="lr-section__grid">
            <Field label="요청 ID">{detail.id}</Field>
            <Field label="신청일">{formatDateTime(detail.created_at)}</Field>
            <Field label="수정일">{formatDateTime(detail.updated_at)}</Field>
          </div>
        </section>
      </div>
    );
  };

  return (
    <Modal open={open} title="강습 요청 상세" width="820px" onClose={onClose}>
      {renderContent()}
    </Modal>
  );
};

export default LessonRequestDetailModal;
